import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, CheckCircle, FlowArrow, UsersThree, ChartLineUp,
  Folders, Lock, Lightning, Star,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1576731753569-3e93a228048c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEATURES = [
  { icon: FlowArrow, title: "Sequential reviews", body: "Route every contract through Grant Manager, Specialist and Legal with SLA timers and full audit history." },
  { icon: UsersThree, title: "Grantee portal", body: "Give funded partners a limited login to submit reports and reimbursements — scoped to just their awards." },
  { icon: ChartLineUp, title: "Leadership analytics", body: "Funding by source, pipeline by stage and overdue trends — exportable to PDF, Excel and CSV." },
  { icon: Folders, title: "Document control", body: "Versioned SOWs, budgets and backup on encrypted storage, with approve / reject review workflows." },
  { icon: Lock, title: "Multi-org security", body: "Every organization's data is fully isolated. Your contracts are never visible to another tenant." },
  { icon: Lightning, title: "Bulk operations", body: "Reassign reviewers across dozens of contracts, import in bulk, and clear SLA-breach escalations fast." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => { if (!loading && user) navigate("/dashboard", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => { api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const startTrial = () => navigate("/login?mode=register");

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-primary text-white">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="landing-brand">
            <ShieldCheck size={26} weight="fill" className="text-gold" />
            <span className="font-head font-bold text-lg">grantSME</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="landing-signin" onClick={() => navigate("/login")}>Sign in</Button>
            <Button className="bg-gold text-primary hover:bg-gold/90" data-testid="landing-cta-nav" onClick={startTrial}>Start free trial</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-primary text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-gold overline mb-5">
              <Star size={14} weight="fill" /> Contract &amp; grant management, done right
            </div>
            <h1 className="font-head font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Run every contract and grant from one <span className="text-gold">accountable</span> workspace.
            </h1>
            <p className="text-white/75 text-lg mt-6 max-w-xl leading-relaxed">
              grantSME gives institutions structured intake, role-based sequential review, grantee collaboration and
              leadership analytics — with each organization's data fully isolated and encrypted.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" className="bg-gold text-primary hover:bg-gold/90" data-testid="landing-cta-hero" onClick={startTrial}>
                Start free trial <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => navigate("/login")}>
                Sign in
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-white/70">
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-gold" /> 14-day trial</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-gold" /> 30% off first month</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-gold" /> No card to start</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-gold/10 rounded-xl blur-2xl" />
            <img src={HERO} alt="grantSME workspace" className="relative rounded-xl shadow-2xl w-full h-[380px] object-cover" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
        <div className="overline text-muted-foreground">Everything your program office needs</div>
        <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight mt-2 max-w-2xl">
          Purpose-built for the full contract &amp; grant lifecycle.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`landing-feature-${f.title.split(" ")[0].toLowerCase()}`} className="rounded-lg border border-border bg-card p-6 hover:border-gold/50 transition-colors">
              <div className="h-11 w-11 rounded-md bg-primary/8 flex items-center justify-center text-primary">
                <f.icon size={22} weight="duotone" />
              </div>
              <h3 className="font-head font-semibold text-lg mt-4">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <div className="overline text-muted-foreground">Simple, seat-based pricing</div>
            <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight mt-2">Plans that scale with your team</h2>
            <p className="text-muted-foreground mt-3">Unlimited contracts and grantee logins on every plan. Pay yearly for ~1.5 months free.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-10">
            {plans.map((p, i) => (
              <div key={p.key} data-testid={`landing-plan-${p.key}`} className={`rounded-lg border p-6 bg-card flex flex-col ${i === 2 ? "border-gold ring-1 ring-gold" : "border-border"}`}>
                <div className="font-head font-bold text-lg">{p.name}</div>
                <div className="mt-2"><span className="font-head font-bold text-3xl">${p.monthly}</span><span className="text-sm text-muted-foreground">/mo</span></div>
                <div className="text-xs text-muted-foreground mt-1">{p.seats >= 100000 ? "Unlimited seats" : `Up to ${p.seats} seats`}</div>
                <p className="text-sm text-muted-foreground mt-3 flex-1">{p.blurb}</p>
                <Button className="mt-5" variant={i === 2 ? "default" : "outline"} data-testid={`landing-plan-cta-${p.key}`} onClick={startTrial}>Start free trial</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16 lg:py-20 text-center">
        <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight">Ready to bring order to your contracts?</h2>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Spin up your organization's workspace in under a minute.</p>
        <Button size="lg" className="mt-7 bg-primary hover:bg-primary/90" data-testid="landing-cta-footer" onClick={startTrial}>
          Start your free trial <ArrowRight size={18} className="ml-1.5" />
        </Button>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><ShieldCheck size={20} weight="fill" className="text-primary" /><span className="font-head font-bold text-foreground">grantSME</span></div>
          <div>© {new Date().getFullYear()} grantSME · Grant Subject Matter Expert</div>
        </div>
      </footer>
    </div>
  );
}
